import axios from "axios";
import { resolveApiBaseUrl } from "./apiBase.util";
import { beginSessionExpiry, errorToastify } from "./toastify.util";

const instance = axios.create({
	timeout: 20000,
	withCredentials: true,
});

export const setAuthToken = (token) => {
	if (!instance.defaults.headers.common) instance.defaults.headers.common = {};
	if (token) {
		instance.defaults.headers.common.Authorization = `Bearer ${token}`;
	} else {
		delete instance.defaults.headers.common.Authorization;
	}
};

let inMemoryCsrfToken = null;

export const setCsrfToken = (token) => {
	inMemoryCsrfToken = token;
};

export const getCookie = (name) => {
	if (typeof document === "undefined") return null;
	const match = document.cookie.match(new RegExp("(^|;\\s*)(" + name + ")=([^;]*)"));
	return match ? decodeURIComponent(match[3]) : null;
};

const inflight = new Map();

function requestKey(config) {
	if (typeof FormData !== "undefined" && config.data instanceof FormData) return null;
	const method = String(config.method || "get").toLowerCase();
	const data = typeof config.data === "string" ? config.data : JSON.stringify(config.data || "");
	const params = JSON.stringify(config.params || "");
	return `${method} ${config.baseURL || ""}${config.url} ${params} ${data}`;
}

instance.interceptors.request.use((config) => {
	config.baseURL = resolveApiBaseUrl();

	// Auto-inject CSRF token on mutation requests
	const method = String(config.method || "get").toUpperCase();
	if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
		const csrf = inMemoryCsrfToken || getCookie("admin_csrf_token");
		if (csrf && !config.headers["X-CSRF-Token"]) {
			config.headers["X-CSRF-Token"] = csrf;
		}
	}

	const url = String(config.url || "");
	const isAuthRoute = /\/auth\/(login|me|resend-otp|forgot-password|reset-password)/.test(url);
	if (isAuthRoute) return config;

	const isLongPackWrite =
		/\/mst-challenge\/(create|update)|\/mst-hunt\/(create|update)|\/item\/(create-group|update-group)/.test(
			url,
		);
	if (isLongPackWrite) return config;

	const key = requestKey(config);
	if (!key) return config;
	const existing = inflight.get(key);
	if (existing) {
		config.adapter = () => existing.promise.then((response) => ({
			data: response.data,
			status: response.status,
			statusText: response.statusText,
			headers: response.headers,
			config,
		}));
		return config;
	}
	let resolve;
	let reject;
	const promise = new Promise((res, rej) => {
		resolve = res;
		reject = rej;
	});
	promise.catch(() => {});
	inflight.set(key, { promise, resolve, reject });
	config.__dedupeKey = key;
	config.__dedupeOwner = true;
	return config;
});

let sessionExpiryStarted = false;

export function resetSessionExpiry() {
	sessionExpiryStarted = false;
}

function handleSessionExpired() {
	if (sessionExpiryStarted) return;
	sessionExpiryStarted = true;
	beginSessionExpiry();
	localStorage.removeItem("token");
	localStorage.removeItem("user");
	localStorage.removeItem("staff");
	localStorage.removeItem("menus");
	sessionStorage.removeItem("admin_auth_active");
	setAuthToken("");
	inMemoryCsrfToken = null;
	errorToastify("Your session has expired. Please sign in again.", { id: "session-expired" });
	if (!window.location.pathname.startsWith("/login")) {
		window.location.assign("/login");
	}
}

instance.interceptors.response.use(
	(response) => {
		const key = response.config?.__dedupeKey;
		if (response.config?.__dedupeOwner && key && inflight.has(key)) {
			inflight.get(key).resolve(response);
			inflight.delete(key);
		}

		// Cache CSRF token if returned in response payload
		if (response.data?.result?.csrfToken) {
			inMemoryCsrfToken = response.data.result.csrfToken;
		}

		return response;
	},
	(error) => {
		const key = error.config?.__dedupeKey;
		if (error.config?.__dedupeOwner && key && inflight.has(key)) {
			inflight.get(key).reject(error);
			inflight.delete(key);
		}
		const status = error.response?.status;
		const url = String(error.config?.url || "");
		const isLogin = url.includes("/auth/login") || url.includes("/admin-login") || url.includes("/auth/me");
		if (status === 401 && !isLogin) handleSessionExpired();
		return Promise.reject(error);
	}
);

export default instance;
